/**
 * "Sound when done" — an optional short chime played once a chat turn finishes
 * cleanly with nothing left to run. The preference is stored in localStorage
 * (default OFF) and broadcast on change via a window CustomEvent so every mounted
 * toggle re-reads it live, mirroring the `thunderay-ai:token-changed` event bus.
 */

const STORAGE_KEY = "thunderay-ai:sound-on-done";
/** Fired after the preference changes so live toggles re-read and reflect it. */
export const SOUND_CHANGED_EVENT = "thunderay-ai:sound-changed";

function hasLocalStorage(): boolean {
  try {
    return (
      typeof globalThis !== "undefined" &&
      typeof (globalThis as { localStorage?: Storage }).localStorage !== "undefined"
    );
  } catch {
    return false;
  }
}

/** Whether "play a sound when the prompt is done" is enabled. Default OFF. */
export function isSoundOnDone(): boolean {
  if (!hasLocalStorage()) return false;
  try {
    return globalThis.localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

/** Persist the preference and broadcast the change so live toggles re-read it. */
export function setSoundOnDone(on: boolean): void {
  if (hasLocalStorage()) {
    try {
      globalThis.localStorage.setItem(STORAGE_KEY, on ? "1" : "0");
    } catch {
      /* private-mode / restricted — the event still fires so the UI reflects intent */
    }
  }
  if (typeof window !== "undefined") {
    try {
      window.dispatchEvent(new CustomEvent(SOUND_CHANGED_EVENT));
    } catch {
      /* restricted environment (about:blank, SSR) — ignore */
    }
  }
}

// ---- Audio -------------------------------------------------------------

type AudioContextCtor = new () => AudioContext;

function getAudioContextCtor(): AudioContextCtor | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as {
    AudioContext?: AudioContextCtor;
    webkitAudioContext?: AudioContextCtor;
  };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

// A single shared AudioContext, created lazily on the first chime. Browsers cap
// the number of live contexts, so we never make more than one.
let sharedCtx: AudioContext | null = null;

function getCtx(): AudioContext | null {
  const Ctor = getAudioContextCtor();
  if (!Ctor) return null;
  if (!sharedCtx) {
    try {
      sharedCtx = new Ctor();
    } catch {
      return null;
    }
  }
  return sharedCtx;
}

/**
 * Play a short, gentle two-tone chime (~250ms) via the Web Audio API. Each note
 * has a quick attack and exponential decay so there is no click. No-op when the
 * Web Audio API is unavailable (non-browser/test envs, unsupported browsers) or
 * when the browser blocks playback (autoplay policy before any user gesture).
 */
export function playChime(): void {
  const ctx = getCtx();
  if (!ctx) return;
  try {
    // A context can start suspended (or be auto-suspended); resume before use.
    if (ctx.state === "suspended") void ctx.resume();
    const now = ctx.currentTime;
    // Two ascending sine notes — a soft "ta-da" (A5 → E6).
    const notes = [
      { freq: 880, start: 0, dur: 0.16 },
      { freq: 1318.5, start: 0.1, dur: 0.2 },
    ];
    for (const n of notes) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = n.freq;
      const t0 = now + n.start;
      const peak = 0.15;
      // exponentialRamp can't touch 0, so bracket the envelope with tiny values.
      gain.gain.setValueAtTime(0.0001, t0);
      gain.gain.exponentialRampToValueAtTime(peak, t0 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + n.dur);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t0);
      osc.stop(t0 + n.dur + 0.02);
    }
  } catch {
    /* audio blocked (autoplay policy, no user gesture yet) — ignore */
  }
}
